import React, { useState, useEffect } from 'react';
import axios from '../api/axios';
import { toast } from '../components/Toast';
import './AuditLogPage.css';

function AuditLogPage() {
  const [auditLogs, setAuditLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Pagination
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  
  // Filters
  const [entityTypeFilter, setEntityTypeFilter] = useState('');
  const [actionFilter, setActionFilter] = useState('');
  const [userIdFilter, setUserIdFilter] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  
  // Detail modal
  const [selectedLog, setSelectedLog] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);

  useEffect(() => {
    fetchAuditLogs();
  }, [page, entityTypeFilter, actionFilter, userIdFilter, startDate, endDate]);

  const fetchAuditLogs = async () => {
    setLoading(true);
    try {
      const params = {
        page,
        size: 20
      };
      
      if (entityTypeFilter) params.entityType = entityTypeFilter;
      if (actionFilter) params.action = actionFilter;
      if (userIdFilter) params.userId = userIdFilter;
      if (startDate) params.startDate = new Date(startDate).toISOString();
      if (endDate) params.endDate = new Date(endDate).toISOString();
      
      const response = await axios.get('/audit-logs', { params });
      setAuditLogs(response.data.content || []);
      setTotalPages(response.data.totalPages || 0);
      setTotalElements(response.data.totalElements || 0);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to fetch audit logs');
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = (log) => {
    setSelectedLog(log);
    setShowDetailModal(true);
  };

  const handleCloseDetailModal = () => {
    setShowDetailModal(false);
    setSelectedLog(null);
  };

  const handleResetFilters = () => {
    setEntityTypeFilter('');
    setActionFilter('');
    setUserIdFilter('');
    setStartDate('');
    setEndDate('');
    setPage(0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  const entityLabel = (type) => {
    const map = {
      Receipt: '🧾 Receipt',
      VendorPurchase: '🛒 Vendor Purchase',
      VendorPayment: '💸 Vendor Payment',
      Expense: '💰 Expense',
      Advance: '⏩ Advance',
      Customer: '👤 Customer',
      Project: '🏗 Project',
      Enquiry: '📋 Enquiry',
      Vendor: '🏪 Vendor',
      Employee: '👷 Employee',
      User: '🔑 User',
    };
    return map[type] || type;
  };

  const formatJson = (jsonString) => {
    if (!jsonString) return 'N/A';
    try {
      const obj = JSON.parse(jsonString);
      return JSON.stringify(obj, null, 2);
    } catch (e) {
      return jsonString;
    }
  };

  return (
    <div className="audit-log-page">
      <div className="page-header">
        <h1>Audit Logs</h1>
        <p className="subtitle">View system activity and changes</p>
      </div>

      {/* Filters */}
      <div className="filters-section">
        <div className="filters-grid">
          <div className="filter-group">
            <label>Entity Type</label>
            <select
              value={entityTypeFilter}
              onChange={(e) => { setEntityTypeFilter(e.target.value); setPage(0); }}
            >
              <option value="">All</option>
              <option value="User">User</option>
              <option value="Customer">Customer</option>
              <option value="Project">Project</option>
              <option value="Enquiry">Enquiry</option>
              <option value="Expense">Expense</option>
              <option value="Vendor">Vendor</option>
              <option value="Employee">Employee</option>
              <option value="Receipt">Receipt</option>
              <option value="VendorPurchase">Vendor Purchase</option>
              <option value="VendorPayment">Vendor Payment</option>
              <option value="Advance">Advance</option>
            </select>
          </div>

          <div className="filter-group">
            <label>Action</label>
            <select
              value={actionFilter}
              onChange={(e) => { setActionFilter(e.target.value); setPage(0); }}
            >
              <option value="">All</option>
              <option value="CREATE">Create</option>
              <option value="UPDATE">Update</option>
              <option value="DELETE">Delete</option>
              <option value="LOGIN">Login</option>
              <option value="LOGOUT">Logout</option>
            </select>
          </div>

          <div className="filter-group">
            <label>Start Date</label>
            <input
              type="datetime-local"
              value={startDate}
              onChange={(e) => { setStartDate(e.target.value); setPage(0); }}
            />
          </div>

          <div className="filter-group">
            <label>End Date</label>
            <input
              type="datetime-local"
              value={endDate}
              onChange={(e) => { setEndDate(e.target.value); setPage(0); }}
            />
          </div>
        </div>

        <div className="filter-actions">
          <button onClick={handleResetFilters} className="btn-secondary">
            Reset Filters
          </button>
        </div>
      </div>

      {/* Results Summary */}
      <div className="results-summary">
        <p>Showing {auditLogs.length} of {totalElements} audit logs</p>
      </div>

      {/* Audit Logs Table */}
      {loading ? (
        <div className="loading">Loading audit logs...</div>
      ) : (
        <div className="table-container">
          <table className="audit-log-table">
            <thead>
              <tr>
                <th>Date & Time</th>
                <th>Entity Type</th>
                <th>Entity ID</th>
                <th>Action</th>
                <th>User</th>
                <th>IP Address</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {auditLogs.length === 0 ? (
                <tr>
                  <td colSpan="7" className="no-data">No audit logs found</td>
                </tr>
              ) : (
                auditLogs.map((log) => (
                  <tr key={log.id}>
                    <td data-label="Timestamp"><strong>{formatDate(log.createdAt)}</strong></td>
                    <td data-label="Entity">{entityLabel(log.entityType)}</td>
                    <td data-label="Entity ID">{log.entityId}</td>
                    <td data-label="Action">
                      <span className={`action-badge action-${log.action.toLowerCase()}`}>
                        {log.action}
                      </span>
                    </td>
                    <td data-label="User">{log.userName || 'System'}</td>
                    <td data-label="IP Address">{log.ipAddress || '-'}</td>
                    <td data-label="Actions">
                      <div className="action-buttons">
                        <button
                          onClick={() => handleViewDetails(log)}
                          className="btn btn-sm btn-primary btn-view"
                        >
                          View Details
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="pagination">
          <button
            onClick={() => setPage(page - 1)}
            disabled={page === 0}
            className="btn-pagination"
          >
            Previous
          </button>
          <span className="page-info">
            Page {page + 1} of {totalPages}
          </span>
          <button
            onClick={() => setPage(page + 1)}
            disabled={page >= totalPages - 1}
            className="btn-pagination"
          >
            Next
          </button>
        </div>
      )}

      {/* Detail Modal */}
      {showDetailModal && selectedLog && (
        <div className="modal-overlay" onClick={handleCloseDetailModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Audit Log Details</h2>
              <button onClick={handleCloseDetailModal} className="close-btn">&times;</button>
            </div>
            <div className="modal-body">
              <div className="detail-row">
                <label>ID:</label>
                <span>{selectedLog.id}</span>
              </div>
              <div className="detail-row">
                <label>Date & Time:</label>
                <span>{formatDate(selectedLog.createdAt)}</span>
              </div>
              <div className="detail-row">
                <label>Entity Type:</label>
                <span>{entityLabel(selectedLog.entityType)}</span>
              </div>
              <div className="detail-row">
                <label>Entity ID:</label>
                <span>{selectedLog.entityId}</span>
              </div>
              <div className="detail-row">
                <label>Action:</label>
                <span className={`action-badge action-${selectedLog.action.toLowerCase()}`}>
                  {selectedLog.action}
                </span>
              </div>
              <div className="detail-row">
                <label>User:</label>
                <span>{selectedLog.userName || 'System'} (ID: {selectedLog.userId || 'N/A'})</span>
              </div>
              <div className="detail-row">
                <label>IP Address:</label>
                <span>{selectedLog.ipAddress || '-'}</span>
              </div>
              
              {selectedLog.oldValues && (
                <div className="detail-section">
                  <label>Old Values:</label>
                  <pre className="json-display">{formatJson(selectedLog.oldValues)}</pre>
                </div>
              )}
              
              {selectedLog.newValues && (
                <div className="detail-section">
                  <label>New Values:</label>
                  <pre className="json-display">{formatJson(selectedLog.newValues)}</pre>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button onClick={handleCloseDetailModal} className="btn-secondary">
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AuditLogPage;
